import { useState, useEffect } from "react";
import { X, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { CategoryCard } from "./CategoryCard";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { categories as staticCategories } from "@/data/categories";
import { categoryImages } from "@/data/categoryImages";

interface Category {
  id: string;
  name: string;
  slug: string;
  imageUrl?: string;
}

interface CategoryBrowserProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CategoryBrowser({ isOpen, onClose }: CategoryBrowserProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      // Use static categories with pre-defined images (same as desktop)
      const mappedCategories = staticCategories.map(category => ({
        id: category.id,
        name: i18n.language === 'gr' && category.name_gr ? category.name_gr : category.name,
        slug: category.slug,
        imageUrl: categoryImages[category.id]
      }));
      setCategories(mappedCategories);
      setIsLoading(false);
    }
  }, [isOpen, i18n.language]);

  const handleScrollToTop = useScrollToTop();

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCategoryClick = (slug: string) => {
    handleScrollToTop(`/category/${slug}`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Header */}
      <div className="bg-background border-b border-border shrink-0">
        <div className="flex items-center justify-between p-4">
          <h2 className="text-xl font-display font-semibold">Browse Categories</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg perlla-transition"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        {/* Search */}
        <div className="px-4 pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4" style={{ paddingBottom: '100px' }}>
        {isLoading ? (
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[4/3] bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {filteredCategories.map((category) => (
              <div key={category.id} onClick={() => handleCategoryClick(category.slug)}>
                <CategoryCard 
                  id={category.id}
                  name={category.name}
                  slug={category.slug}
                  image_url={category.imageUrl}
                />
              </div>
            ))}
          </div>
        )}

        {!isLoading && filteredCategories.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No categories found</p>
          </div>
        )}
      </div>
    </div>
  );
}